// V1.0.64: Modal Quản lý thành viên dự án
// Cho phép: xem list member hiện có, thêm member (chọn user + role), đổi role, xóa member.
import { useState, useMemo } from 'react';
import {
  Modal, Select, Button, Tag, Empty, Spin, App as AntdApp, Tooltip,
  Popconfirm, Avatar,
} from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Trash2, User as UserIcon } from 'lucide-react';
import { projectService, type ProjectMember, type ProjectMemberRole } from '@/services/projectService';
import { userService } from '@/services/userService';

interface Props {
  open: boolean;
  projectId: string;
  projectName: string;
  canManage: boolean; // chỉ OWNER/MANAGER/SYS_ADMIN được phép
  onClose: () => void;
}

const ROLE_OPTIONS: { value: ProjectMemberRole; label: string; color: string }[] = [
  { value: 'OWNER',   label: 'Chủ dự án',  color: '#ed8936' },
  { value: 'MANAGER', label: 'Quản trị',   color: '#1976D2' },
  { value: 'MEMBER',  label: 'Thành viên', color: '#5b6875' },
  { value: 'VIEWER',  label: 'Chỉ xem',    color: '#805ad5' },
];

function roleLabel(role: ProjectMemberRole) {
  return ROLE_OPTIONS.find(o => o.value === role)?.label ?? role;
}
function roleColor(role: ProjectMemberRole) {
  return ROLE_OPTIONS.find(o => o.value === role)?.color ?? '#5b6875';
}
function getInitials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(-2).map(p => p[0]?.toUpperCase() ?? '').join('');
}

export function ProjectMembersModal({ open, projectId, projectName, canManage, onClose }: Props) {
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();
  const [newUserId, setNewUserId] = useState<string | undefined>();
  const [newRole, setNewRole] = useState<ProjectMemberRole>('MEMBER');

  // Load members list
  const { data: members = [], isLoading } = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => projectService.getMembers(projectId),
    enabled: open && !!projectId,
  });

  // Load all users for "Add member" dropdown
  const { data: usersResp } = useQuery({
    queryKey: ['users', 'for-project-member-picker'],
    queryFn: () => userService.list({ pageSize: 1000 }),
    enabled: open && canManage,
  });
  const allUsers = usersResp?.items ?? [];

  // Filter out users already in project from "Add" dropdown
  const memberIds = useMemo(() => new Set(members.map(m => m.userId)), [members]);
  const addableUsers = useMemo(
    () => allUsers.filter(u => !memberIds.has(u.id)),
    [allUsers, memberIds],
  );

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!newUserId) throw new Error('Chưa chọn user');
      await projectService.addMember(projectId, newUserId, newRole);
    },
    onSuccess: () => {
      message.success('Đã thêm thành viên');
      setNewUserId(undefined);
      setNewRole('MEMBER');
      qc.invalidateQueries({ queryKey: ['project-members', projectId] });
      qc.invalidateQueries({ queryKey: ['project-detail', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e: any) => {
      message.error('Thêm thất bại: ' + (e?.response?.data?.message ?? e.message));
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async (vars: { userId: string; role: ProjectMemberRole }) => {
      await projectService.updateMemberRole(projectId, vars.userId, vars.role);
    },
    onSuccess: () => {
      message.success('Đã cập nhật vai trò');
      qc.invalidateQueries({ queryKey: ['project-members', projectId] });
      qc.invalidateQueries({ queryKey: ['project-detail', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e: any) => {
      message.error('Cập nhật thất bại: ' + (e?.response?.data?.message ?? e.message));
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      await projectService.removeMember(projectId, userId);
    },
    onSuccess: () => {
      message.success('Đã xóa thành viên');
      qc.invalidateQueries({ queryKey: ['project-members', projectId] });
      qc.invalidateQueries({ queryKey: ['project-detail', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (e: any) => {
      message.error('Xóa thất bại: ' + (e?.response?.data?.message ?? e.message));
    },
  });

  // Sort: OWNER trước → MANAGER → MEMBER → VIEWER
  const sortedMembers = useMemo(() => {
    const order: Record<ProjectMemberRole, number> = { OWNER: 0, MANAGER: 1, MEMBER: 2, VIEWER: 3 };
    return [...members].sort((a, b) => {
      const oa = order[a.role] ?? 99;
      const ob = order[b.role] ?? 99;
      if (oa !== ob) return oa - ob;
      return a.fullName.localeCompare(b.fullName, 'vi');
    });
  }, [members]);

  // Count OWNERs để không cho remove/downgrade nếu là OWNER duy nhất
  const ownerCount = members.filter(m => m.role === 'OWNER').length;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={<Button onClick={onClose}>Đóng</Button>}
      title={`Quản lý thành viên — ${projectName}`}
      width={680}
      destroyOnClose
    >
      {/* Thêm thành viên (chỉ canManage mới hiện) */}
      {canManage && (
        <div style={{
          padding: 12,
          background: '#f7faff',
          border: '1px solid #d6e4ff',
          borderRadius: 6,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1976D2', marginBottom: 8 }}>
            <UserPlus size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
            THÊM THÀNH VIÊN MỚI
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Select
              showSearch
              placeholder="Chọn nhân sự..."
              value={newUserId}
              onChange={setNewUserId}
              style={{ flex: 1, minWidth: 260 }}
              optionFilterProp="label"
              options={addableUsers.map(u => ({
                value: u.id,
                label: `${u.fullName}${u.position ? ` — ${u.position}` : ''}`,
              }))}
              notFoundContent={addableUsers.length === 0 ? 'Tất cả user đã ở trong dự án' : undefined}
            />
            <Select
              value={newRole}
              onChange={setNewRole}
              style={{ width: 140 }}
              options={ROLE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
            />
            <Button
              type="primary"
              icon={<UserPlus size={14} />}
              loading={addMutation.isPending}
              disabled={!newUserId}
              onClick={() => addMutation.mutate()}
            >
              Thêm
            </Button>
          </div>
        </div>
      )}

      {/* Danh sách thành viên hiện tại */}
      <div style={{ fontSize: 12, fontWeight: 600, color: '#5b6875', marginBottom: 8 }}>
        DANH SÁCH THÀNH VIÊN ({members.length})
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : sortedMembers.length === 0 ? (
        <Empty description="Chưa có thành viên" />
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #f0f4f8', borderRadius: 4 }}>
          {sortedMembers.map((m, idx) => {
            const isLastOwner = m.role === 'OWNER' && ownerCount === 1;
            return (
              <div
                key={m.userId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderBottom: idx < sortedMembers.length - 1 ? '1px solid #f0f4f8' : 'none',
                  background: idx % 2 === 0 ? '#fff' : '#fafbfc',
                }}
              >
                <Avatar
                  size={36}
                  src={m.avatarUrl ?? undefined}
                  style={{ background: '#1976D2', flexShrink: 0 }}
                >
                  {!m.avatarUrl && getInitials(m.fullName)}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1e23' }}>
                    {m.fullName}
                  </div>
                  {m.position && (
                    <div style={{ fontSize: 11, color: '#7d8a98' }}>
                      {m.position}
                    </div>
                  )}
                </div>
                {canManage ? (
                  <>
                    <Tooltip
                      title={isLastOwner ? 'Không thể đổi vai trò Chủ dự án duy nhất' : ''}
                    >
                      <Select
                        value={m.role}
                        onChange={(role) => updateRoleMutation.mutate({ userId: m.userId, role })}
                        style={{ width: 130 }}
                        size="small"
                        disabled={isLastOwner || updateRoleMutation.isPending}
                        options={ROLE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="Xóa thành viên?"
                      description={`Bỏ ${m.fullName} khỏi dự án?`}
                      okText="Xóa"
                      okType="danger"
                      cancelText="Hủy"
                      disabled={isLastOwner}
                      onConfirm={() => removeMutation.mutate(m.userId)}
                    >
                      <Tooltip title={isLastOwner ? 'Không thể xóa Chủ dự án duy nhất' : 'Xóa thành viên'}>
                        <Button
                          icon={<Trash2 size={14} />}
                          danger
                          size="small"
                          type="text"
                          disabled={isLastOwner}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </>
                ) : (
                  <Tag color={roleColor(m.role)} style={{ marginRight: 0 }}>
                    {roleLabel(m.role)}
                  </Tag>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
